import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import {
    requiredValueConfig,
    countrySpecificOrganizationNumberValidationConfig,
    organizationNumberFormatHint,
    allowedUrlsValidationConfig,
} from '../../../../types/ValidationTypes';
import { parseUrlLines } from '../../../../services/helpers/urlList';
import Spinner from '../../../atoms/Spinner/Spinner';
import ErrorMessage from '../../../atoms/Message/Error/ErrorMessage';
import ModalContainer from '../../../atoms/Modal/ModalContainer';
import HeaderTitle from '../../../atoms/Modal/HeaderTitle';
import FormRow from '../../../atoms/Form/FormRow';
import GridInput from '../../../atoms/Input/GridInput';
import GridTextArea from '../../../atoms/Input/GridTextArea';
import FooterButtonContainer from '../../../atoms/Modal/FooterButtonContainer';
import { ErrorType } from '../../../../types/response/ErrorCodes';
import SuccessMessage from '../../../atoms/Message/Success/SuccessMessage';
import Button from '../../../atoms/Button/Button';
import SelectInput from '../../../atoms/Input/SelectInput';
import { merchantCountryOptions } from './merchantCountryOptions';
import { AddMerchantRequest, getMerchantStatus } from '../../../../types/onboarding/Merchant';
import { useOnboardingStore } from '../../../../store/admin/onboarding/onboardingStore';
import { allowedUrlsHint } from '../Organizations/CreateOrganizationForm';

interface AddMerchantFormProps {
    /** Name of the organization the merchant is added to, shown in the header. */
    organizationName: string;
    onAdd: (request: AddMerchantRequest) => void;
    hide: () => void;
    theme: DefaultTheme;
}

type Inputs = {
    merchantName: string;
    organizationNumber: string;
    /** One https:// URL per line; parsed into the allowed URLs of the merchant on submit. */
    allowedUrls: string;
};

const defaultValues: Inputs = {
    merchantName: '',
    organizationNumber: '',
    allowedUrls: '',
};

/**
 * Adds one merchant to an existing organization, collecting the same merchant fields as at organization
 * onboarding (CreateOrganizationForm). After a merchant is added the form can be used again for the next one.
 * Errors are kept in the onboarding store, so the entered values stay in place for a retry.
 */
const AddMerchantForm: React.FC<AddMerchantFormProps> = ({
    organizationName,
    onAdd,
    hide,
    theme,
}: AddMerchantFormProps) => {
    const { isAddingMerchant, addedMerchant, addMerchantError, clearAddMerchant } =
        useOnboardingStore();

    const [country, setCountry] = useState('SE');

    const form = useForm<Inputs>({
        mode: 'onSubmit',
        shouldUseNativeValidation: true,
        defaultValues,
    });
    const { getValues, register, handleSubmit, reset } = form;

    const merchantName = register('merchantName', requiredValueConfig);
    const organizationNumber = register(
        'organizationNumber',
        countrySpecificOrganizationNumberValidationConfig(country)
    );
    const allowedUrls = register('allowedUrls', allowedUrlsValidationConfig);

    const isAdded = addedMerchant !== undefined;
    const isDisabled = isAddingMerchant || isAdded;

    const onSubmit = (data: Inputs) => {
        onAdd({
            countryCode: country,
            organizationNumber: data.organizationNumber,
            name: data.merchantName,
            allowedUrls: parseUrlLines(data.allowedUrls),
        });
    };

    // The country is kept: merchants added in one go usually share it.
    const onAddAnother = () => {
        clearAddMerchant();
        reset(defaultValues);
    };

    return (
        <>
            <ModalContainer position="header">
                <HeaderTitle>Add merchant</HeaderTitle>
            </ModalContainer>

            <ModalContainer position="content" noScroll={false}>
                <form onSubmit={handleSubmit(onSubmit)} data-testid="form" id="add-merchant-form">
                    <InputContainer>
                        <SubtleText>
                            The legal entity registered under the organization number. It is added
                            to {organizationName} with status Pending.
                        </SubtleText>
                        <FormRow first>
                            <GridInput
                                type="text"
                                id="merchantName"
                                placeholder="Merchant legal name"
                                placeholderRaised={!!getValues(merchantName.name)}
                                setRef={merchantName.ref}
                                name={merchantName.name}
                                onChange={merchantName.onChange}
                                disabled={isDisabled}
                                dataTestId="merchant-name"
                            />
                        </FormRow>
                        <FormRow last>
                            <SelectInput
                                id="country"
                                dataTestId="add-merchant-country-selector"
                                placeholder="Country"
                                onChange={setCountry}
                                value={country}
                                options={merchantCountryOptions}
                                disabled={isDisabled}
                                setHeight={false}
                            />
                            <GridInput
                                type="text"
                                id="organizationNumber"
                                placeholder="Organization number"
                                placeholderRaised={!!getValues(organizationNumber.name)}
                                setRef={organizationNumber.ref}
                                name={organizationNumber.name}
                                onChange={organizationNumber.onChange}
                                disabled={isDisabled}
                                dataTestId="organization-number"
                            />
                        </FormRow>
                        <Hint data-testid="organization-number-hint">
                            {organizationNumberFormatHint(country)}
                        </Hint>
                        <UrlsContainer>
                            <FormRow first last>
                                <GridTextArea
                                    id="allowedUrls"
                                    placeholder="Allowed store URLs"
                                    placeholderRaised={!!getValues(allowedUrls.name)}
                                    setRef={allowedUrls.ref}
                                    name={allowedUrls.name}
                                    onChange={allowedUrls.onChange}
                                    disabled={isDisabled}
                                    dataTestId="allowed-urls"
                                />
                            </FormRow>
                            <Hint>{allowedUrlsHint}</Hint>
                        </UrlsContainer>
                    </InputContainer>

                    {addedMerchant && (
                        <ResultContainer data-testid="add-merchant-success">
                            <SuccessMessage>
                                {addedMerchant.name} was added with status{' '}
                                {getMerchantStatus(addedMerchant.merchant)}.
                            </SuccessMessage>
                        </ResultContainer>
                    )}
                </form>
            </ModalContainer>

            {addMerchantError && (
                <ModalContainer position="error">
                    <ErrorMessage error={addMerchantError} errorHeader={ErrorType.AddMerchant} />
                </ModalContainer>
            )}

            <ModalContainer position="footer">
                <FooterButtonContainer>
                    {!isAdded && (
                        <Button onClick={hide} large>
                            Cancel
                        </Button>
                    )}
                    {!isAdded && (
                        <Button
                            tabIndex={0}
                            form="add-merchant-form"
                            type="submit"
                            large
                            primary
                            disabled={isDisabled}
                            data-testid="add-merchant-submit-button"
                        >
                            {isAddingMerchant ? (
                                <Spinner color={theme.colors.light} size={8} loading />
                            ) : (
                                <span>Add merchant</span>
                            )}
                        </Button>
                    )}
                    {isAdded && (
                        <Button onClick={hide} large>
                            Close
                        </Button>
                    )}
                    {isAdded && (
                        <Button
                            onClick={onAddAnother}
                            large
                            primary
                            data-testid="add-another-merchant-button"
                        >
                            Add another merchant
                        </Button>
                    )}
                </FooterButtonContainer>
            </ModalContainer>
        </>
    );
};

const InputContainer = styled.div`
    display: block;
    padding-bottom: ${props => props.theme.layout.padding.small};
`;

const ResultContainer = styled.div`
    display: flex;
    flex-flow: row;
    justify-content: center;
    padding-bottom: 2.5rem;
`;

const Hint = styled.div`
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
    margin-top: 0.5rem;
`;

const SubtleText = styled.div`
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
    margin-bottom: 1rem;
`;

const UrlsContainer = styled.div`
    margin-top: 1.5rem;
`;

export default withTheme(AddMerchantForm) as React.ComponentType<
    Omit<AddMerchantFormProps, 'theme'>
>;
